(() => {
  "use strict";

  // ===== Selectors =====
  const SELECTOR_FORM = ".js-card-variant[data-card]";
  const SELECTOR_VARIANTS_JSON = ".card-variants-json";
  const SELECTOR_VARIANTS_MONEY_JSON = ".card-variants-money-json"; // NEW: preformatted prices
  const SELECTOR_INPUT_ID = ".product-variant-id";
  const SELECTOR_PRICE_WRAP = "[data-price-wrap]";
  const SELECTOR_ATC = "[data-atc]";
  const SELECTOR_ATC_TEXT = "[data-atc-text]";
  const SELECTOR_TITLE_LINK = ".card__heading a.full-unstyled-link";
  const SELECTOR_BADGE_WRAP = ".card__badge";
  const SELECTOR_RADIO = ".card-swatch-input";
  const SELECTOR_SWATCH = ".card-swatch";

  // ===== Money helpers =====
  // Prefer Liquid-preformatted strings from JSON; otherwise use a simple fallback.
  const formatMoneyFallback = (cents) => {
    if (cents == null) return "";
    if (typeof cents === "string") cents = parseInt(cents, 10);
    return (cents / 100).toFixed(2);
  };

  const getFormattedMoneyEntry = (formEl, variantId) => {
    try {
      const txt = formEl.querySelector(SELECTOR_VARIANTS_MONEY_JSON)?.textContent;
      if (!txt) return null;
      const arr = JSON.parse(txt);
      if (!Array.isArray(arr)) return null;
      return arr.find(m => m && m.id === variantId) || null;
    } catch (e) {
      return null;
    }
  };

  // ===== Option helpers =====
  const getOptionPositions = (formEl) => {
    const radios = Array.from(formEl.querySelectorAll(SELECTOR_RADIO));
    const positions = [...new Set(radios.map(r => parseInt(r.dataset.optionPosition, 10)).filter(Boolean))];
    return positions.sort((a,b) => a - b);
  };

  const collectSelections = (formEl) => {
    const positions = getOptionPositions(formEl);
    return positions.map(pos => {
      const checked = formEl.querySelector(`${SELECTOR_RADIO}[data-option-position="${pos}"]:checked`);
      return checked ? checked.value : "";
    });
  };

  const findMatchingVariant = (variants, selectedOptions) =>
    variants.find(v => v.options.every((opt, idx) => opt === selectedOptions[idx])) || null;

  // ===== UI update =====
  const updateCardUI = (formEl, variant) => {
    const inputId = formEl.querySelector(SELECTOR_INPUT_ID);
    const priceWrap = formEl.querySelector(SELECTOR_PRICE_WRAP);
    const atcBtn = formEl.querySelector(SELECTOR_ATC);
    const atcText = formEl.querySelector(SELECTOR_ATC_TEXT);
    const titleLink = formEl.closest(".card__content")?.querySelector(SELECTOR_TITLE_LINK)
                   || formEl.parentElement?.querySelector(SELECTOR_TITLE_LINK);
    const badgeWrap = formEl.closest(".card")?.querySelector(SELECTOR_BADGE_WRAP);

    // No match: disable ATC and stop
    if (!variant) {
      if (inputId) inputId.value = "";
      if (atcBtn) atcBtn.disabled = true;
      if (atcText) atcText.textContent = "Unavailable";
      return;
    }

    // Variant id for Add to Cart
    if (inputId) inputId.value = variant.id;

    // Price
    if (priceWrap) {
      // Try preformatted entry
      const entry = getFormattedMoneyEntry(formEl, variant.id);

      // Flexible node lookups (supports Dawn-like or your custom markup)
      const currentEl =
        priceWrap.querySelector("[data-price-current]") ||
        priceWrap.querySelector(".price .price__regular .price-item, .price-item--regular");
      const compareEl =
        priceWrap.querySelector("[data-price-compare]") ||
        priceWrap.querySelector(".price .price__sale .price-item--compare");

      const priceText = entry?.price_formatted ?? formatMoneyFallback(variant.price);
      const compareText = entry?.compare_at_formatted ??
                          (variant.compare_at_price ? formatMoneyFallback(variant.compare_at_price) : "");

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (currentEl) currentEl.textContent = priceText;
        if (compareEl) compareEl.textContent = compareText;
        priceWrap.setAttribute("data-on-sale", "true");
      } else {
        if (currentEl) currentEl.textContent = priceText;
        if (compareEl) compareEl.textContent = "";
        priceWrap.removeAttribute("data-on-sale");
      }
    }

    // ATC state/text
    if (atcBtn && atcText) {
      if (variant.available) {
        atcBtn.disabled = false;
        atcText.textContent = (window.theme?.strings?.addToCart || "Add to cart");
      } else {
        atcBtn.disabled = true;
        atcText.textContent = (window.theme?.strings?.soldOut || "Sold out");
      }
    }

    // Update PDP link with variant param
    if (titleLink) {
      try {
        const url = new URL(titleLink.href, location.origin);
        url.searchParams.set("variant", variant.id);
        titleLink.href = url.toString();
      } catch (e) {}
    }

    // Badge state
    if (badgeWrap) {
      const badge = badgeWrap.querySelector('[id^="Badge-"], [id^="NoMediaStandardBadge-"]');
      if (badge) {
        if (!variant.available) {
          badge.textContent = (window.theme?.strings?.soldOut || "Sold out");
          badge.style.display = "";
        } else if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          badge.textContent = (window.theme?.strings?.onSale || "On sale");
          badge.style.display = "";
        } else {
          badge.style.display = "none";
        }
      }
    }
  };

  const updateSwatchPressedStates = (formEl) => {
    formEl.querySelectorAll(SELECTOR_SWATCH).forEach(el => {
      const input = el.previousElementSibling;
      if (input && input.matches(SELECTOR_RADIO)) {
        el.setAttribute("aria-pressed", input.checked ? "true" : "false");
      }
    });
  };

  // ===== Flow =====
  const handleChange = (formEl) => {
    try {
      updateSwatchPressedStates(formEl);

      const variantsTxt = formEl.querySelector(SELECTOR_VARIANTS_JSON)?.textContent;
      if (!variantsTxt) return;
      const variants = JSON.parse(variantsTxt);

      const selectedOptions = collectSelections(formEl);
      const variant = findMatchingVariant(variants, selectedOptions);

      updateCardUI(formEl, variant);
    } catch (e) {
      // Silent per-card failure
    }
  };

  const initForm = (formEl) => {
    formEl.querySelectorAll(SELECTOR_RADIO).forEach(radio => {
      radio.addEventListener("change", () => handleChange(formEl), { passive: true });
    });
    handleChange(formEl); // initial sync
  };

  const init = () => {
    document.querySelectorAll(SELECTOR_FORM).forEach(initForm);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
